import {makeObservable, observable, reaction, runInAction, toJS} from 'mobx';

import {delay} from '@yourcoach/async-utils';

import type {BNUserCategoriesListParams} from '../types/bnrks/methods';
import type {CoreUserCategoriesListParams} from '../types/core/methods';
import type {CategoryApiModel} from './types';

import {envManager} from '../../utils/env';
import {storage} from '../../utils/storage';
import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';

export const STORAGE_KEY_CATEGORIES = 'categories';

export type CleanedCategory = Omit<
  CategoryApiModel,
  'counters' | 'created' | 'updated'
>;

export class CategoryStore<
  T extends CategoryApiModel = CategoryApiModel,
> extends EntityStore<T> {
  categories: T[] = [];
  categoryLookup = new Map<string, CleanedCategory>();

  constructor(...args: ConstructorParameters<typeof EntityStore>) {
    super(...args);

    makeObservable(this, {
      categories: observable,
      categoryLookup: observable.shallow,
    });

    reaction(
      () => this.categories,
      () => {
        if (this.categories.length) {
          this.categoryLookup.clear();

          this.categories.map(this._cleanupCategory).forEach(category => {
            this.categoryLookup.set(category._id, category);
          });

          storage.setItem(
            this.getStorageKey(),
            JSON.stringify(toJS(this.categories)),
          );
        }
      },
    );
  }

  _cleanupCategory(category: CategoryApiModel): CleanedCategory {
    return {
      _id: category._id,
      icon: category.icon,
      level: category.level,
      parent_ids: category.parent_ids,
      slug: category.slug,
      title: category.title,
      type: category.type,
      weight: category.weight,
    };
  }

  async _restoreCategories() {
    try {
      if (this.categories.length) {
        return true;
      }

      const categories = JSON.parse(
        await storage.getItem(this.getStorageKey()),
      ) as T[];

      if (categories) {
        runInAction(() => {
          this.categories = categories;

          this.fetchCategories();
        });

        return true;
      }

      return false;
    } catch (error) {
      /* istanbul ignore next */
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Error restoring categories', error);
      }

      storage.removeItem(this.getStorageKey());

      return false;
    }
  }

  async fetchCategories() {
    type Params = BNUserCategoriesListParams | CoreUserCategoriesListParams;

    const params: Params = {
      limit: 1000,
      query: [['type', '==', 'program']],
      sort: [['created', -1]],
    };

    try {
      const result = await apiRequest<Params>({
        method: 'user.categories.list',
        params,
      });

      runInAction(() => {
        this.categories = result._items;
      });
    } catch (error) {
      await delay(2000);
      await this.fetchCategories();
    }
  }

  getStorageKey() {
    const prefix = envManager.get('isB2b') ? 'b2b_' : '';

    return `${prefix}${STORAGE_KEY_CATEGORIES}`;
  }
}

export const categoryStore = new CategoryStore('category');
